import { useMutation } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import { FcGoogle } from 'react-icons/fc';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { localStore } from '../../v2/js/utils/storage';
import {
  ApiError,
  AuthService,
  GoogleSocialLogin,
  RegistrationStatusEnum,
  UserTypeEnum,
} from '../api';
import { Logo } from '../app/components/Logo';
import { SessionContext } from '../app/contexts/SessionContext';
import { useRequestStatus } from '../app/hooks/requests-status';
import { useDocumentTitle } from '../app/hooks/use-document-title';
import { handleApiError } from '../app/utils/errorhandling';

// Mirrors SOCIAL_LOGIN_IDENTITY_MISMATCH_CODE in accounts/dj_rest_auth_serializers.py
const SOCIAL_IDENTITY_MISMATCH_CODE = 'social_identity_mismatch';

/**
 * The invited address that `GoogleLoginRedirect` packed into the OAuth state, if
 * any. Read only to re-pin a retry — the backend does its own validation, so a
 * malformed or absent value simply leaves the retry unpinned.
 */
const readExpectedEmailFromState = (state: string | null): string | null => {
  if (!state) {
    return null;
  }
  try {
    return JSON.parse(atob(state.replace(/-/g, '+').replace(/_/g, '/')))?.expected_email ?? null;
  } catch {
    return null;
  }
};

export const SocialLogin = () => {
  useDocumentTitle('Social network login');
  const navigate = useNavigate();
  const requestStatus = useRequestStatus();
  const [hasFailed, setHasFailed] = useState(false);
  const [isIdentityMismatch, setIsIdentityMismatch] = useState(false);
  const hasAttemptedRef = useRef(false);

  const { socialApp } = useParams();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const state = searchParams.get('state');

  const { user } = useContext(SessionContext);

  // Stable mutation - won't change on re-renders
  const mutationGoogleSocialLogin = useMutation({
    mutationFn: async (data: GoogleSocialLogin) => {
      requestStatus.setInProgress();
      return AuthService.authSocialLoginGoogleCreate({
        requestBody: data,
      });
    },
    onSuccess: async (userData) => {
      requestStatus.setCompleted();

      const nextUrl = searchParams.get('next') || localStore.get('sessionContinueUrl') || null;

      // Users who still owe us registration details are sent to finish first.
      // `sessionContinueUrl` is deliberately left in place for them — the
      // completion screen consumes it once they are done, so a deep link that
      // started the sign-in isn't lost.
      if (userData?.registration_status === RegistrationStatusEnum.MISSING_INFO) {
        window.location.href = '/register/complete';
        return;
      }

      localStore.remove('sessionContinueUrl');
      window.location.href = nextUrl ? decodeURIComponent(nextUrl) : '/app';
    },

    onError: (errors: ApiError) => {
      requestStatus.setFailed();
      setHasFailed(true);
      // The identity check refused this sign-in: Google returned an account
      // other than the one the invitation was addressed to.
      setIsIdentityMismatch(
        !!(errors as any)?.body?.code?.includes?.(SOCIAL_IDENTITY_MISMATCH_CODE)
      );
      // A stale continue-url would otherwise be applied to whoever signs in
      // next, sending them to a deep link that isn't theirs.
      localStore.remove('sessionContinueUrl');
      // Reset ref on error to allow retry
      hasAttemptedRef.current = false;
      // Reported, not shown: the server message is deliberately generic and the
      // page renders its own copy below.
      handleApiError(errors, { showToast: false });
    },
  });

  // Stable callback that won't change unless code/state change
  const handleGoogleCallback = useCallback(async () => {
    if (
      socialApp === 'google' &&
      code &&
      state &&
      !requestStatus.isInProgress &&
      !hasAttemptedRef.current
    ) {
      hasAttemptedRef.current = true;

      try {
        await mutationGoogleSocialLogin.mutateAsync({ code, state });
      } catch (error) {
        // Error handling is done in mutation's onError
      }
    }
  }, [socialApp, code, state, requestStatus.isInProgress, mutationGoogleSocialLogin]);

  // Handle redirect if user is already logged in
  useEffect(() => {
    if (user) {
      navigate('/app');
    }
  }, [navigate, user]);

  // Handle Google OAuth callback - only run once per code/state combo
  useEffect(() => {
    handleGoogleCallback();
  }, [handleGoogleCallback]);

  // The provider handed us back an error (or the user denied consent), so there
  // is nothing to exchange and no request to make.
  const providerError = searchParams.get('error');
  const showFailure = hasFailed || !!providerError || (!!socialApp && (!code || !state));

  // Carry the invited address into the retry so it stays pinned to that
  // identity; without it, retrying would accept any account again.
  const retryParams = new URLSearchParams({ user_type: UserTypeEnum.PROVIDER });
  const expectedEmail = readExpectedEmailFromState(state);
  if (expectedEmail) {
    retryParams.set('expected_email', expectedEmail);
  }

  if (showFailure) {
    return (
      <div className="login-bg-container">
        <Container>
          <Card>
            <Card.Body>
              <div className="logo-container">
                <Logo className="logo" variant="color" />
              </div>
              <h5 className="tw:text-center tw:mb-2">
                {isIdentityMismatch ? 'Wrong Google account' : "We couldn't sign you in"}
              </h5>
              {/* The mismatch case can be specific — the user already knows which
                  address was invited, it's in the link they followed. Every other
                  failure stays generic: this page must not reveal whether an
                  account exists for an address, nor how it signs in. */}
              <p className="tw:text-center tw:text-zinc-600 tw:text-sm tw:mb-4">
                {isIdentityMismatch
                  ? "That Google account doesn't match the address your invitation was sent to. Try again and pick the matching account, or set a password instead."
                  : 'Something went wrong finishing your sign-in. Please try again, or sign in with your email and password.'}
              </p>
              <div className="d-grid gap-2">
                <Button
                  variant="outline-primary"
                  size="lg"
                  className="d-flex align-items-center justify-content-center w-100"
                  href={`${window.GOOGLE_LOGIN_REDIRECT_URL}?${retryParams.toString()}`}
                >
                  <FcGoogle className="me-2" />
                  <span>Try again with Google</span>
                </Button>
                {/* An invited user has no password yet, so /login is a dead end
                    for them. Send them back to the invitation instead, where the
                    details they already entered are restored and they can set
                    one. */}
                {expectedEmail ? (
                  <Button
                    size="lg"
                    className="w-100"
                    onClick={() =>
                      navigate(
                        `/register/provider/sourced?email=${encodeURIComponent(expectedEmail)}`
                      )
                    }
                  >
                    Set a password instead
                  </Button>
                ) : (
                  <Button size="lg" className="w-100" onClick={() => navigate('/login')}>
                    Sign in with email
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <Container className="h-100" fluid>
      <Row>
        <Col className="col-login" xs={12}>
          <Container
            className="content-wrapper justify-content-center align-items-center text-center"
            fluid
          >
            <Spinner animation="grow" className="loading-spinner" variant="primary" />
          </Container>
        </Col>
      </Row>
    </Container>
  );
};
